import { IDataReaderClient } from "@multiweb/clients/dataReader";
import { bridge } from "@multiweb/reactCore/bridge";
import { ParticipantType } from "../../../../enums";
import { IParticipant } from "../../../../types";
import { UiModelCreator } from "../../../../UiModelCreator";

const { getDataReaderClient } = bridge;

/**
 * Vrací seznam vybratelných hráčů pro aktivitu.
 * @param choosableParticipants Seznam všech existujících hráčů.
 * @param selectedParticipants Seznam hráčů, které už aktivita obsahuje.
 * @param keyword Klíčové slovo podle kterého se mají hráči filtrovat.
 */
export const getAvailableParticipants = (choosableParticipants: readonly IParticipant[], selectedParticipants: readonly IParticipant[], keyword: string): IParticipant[] => {
    const lowerCaseKeyword = keyword.toLowerCase();

    return choosableParticipants.filter(participant => {
        const isNotSelected = selectedParticipants.every(selectedParticipant => selectedParticipant.identifier !== participant.identifier);
        const isUserOrRole = () => participant.participantType === ParticipantType.Role || participant.participantType === ParticipantType.User;
        const includesNameKeyword = () => participant.name.toLowerCase().includes(lowerCaseKeyword);

        return isNotSelected && (isUserOrRole() || includesNameKeyword());
    });
};

/**
 * Funkce slouží pro načtení všech hráčů v daném rozsahu a informuje i o tom, jestli lze v načítání pokračovat.
 * @param startIndex Počáteční index, od kterého se mají data načíst.
 * @param count Počet dat k načtení.
 * @param keyword Klíčové slovo, které se použije v podmínce pro název hráče.
 * @returns Tuple kde na prvním místě je pole hráčů a na druhém místě je boolean informující o tom, zdali lze načíst další hráče.
 */
export const loadParticipants = async (startIndex: number, count: number, keyword: string): Promise<[IParticipant[], boolean]> => {
    const dataReaderClient = getDataReaderClient();
    const [roles, hasMoreRoles] = await batchifyParticipants(count, (newCount) => loadRoles(startIndex, newCount, keyword, dataReaderClient));

    if (hasMoreRoles)
        return [roles, true];

    const [users, hasMoreUsers] = await batchifyParticipants(count, (newCount) => loadUsers(startIndex, newCount, keyword, dataReaderClient));

    return [[...roles, ...users], hasMoreUsers];
};

/** Načte seznam hráčů, kteří jsou typu Role. */
const loadRoles = async (startIndex: number, count: number, keyword: string, dataReaderClient: IDataReaderClient): Promise<IParticipant[]> => {
    const condition = !keyword ? "" : ` and (Fyzicke_jmeno like '%${keyword}%')`;
    const dataRows = await dataReaderClient.getDataRowsWithParams(
        "CRole",
        ["Fyzicke_jmeno"],
        ("ID <> 0" + condition),
        { orderBy: ["Fyzicke_jmeno"], startIndex, count },
    );

    return dataRows.map(dataRow => UiModelCreator.createRoleParticipant(dataRow["Fyzicke_jmeno"].toString(), dataRow["ID"].toString()));
};

/** Načte seznam hráčů, kteří jsou typu User. */
const loadUsers = async (startIndex: number, count: number, keyword: string, dataReaderClient: IDataReaderClient): Promise<IParticipant[]> => {
    const condition = !keyword ? "" : ` and (Uzivatelske_jmeno like '%${keyword}%')`;
    const dataRows = await dataReaderClient.getDataRowsWithParams(
        "CUzivatel",
        ["Uzivatelske_jmeno"],
        ("ID <> 0" + condition),
        { orderBy: ["Uzivatelske_jmeno"], startIndex, count },
    );

    return dataRows.map(dataRow => UiModelCreator.createUserParticipant(dataRow["Uzivatelske_jmeno"].toString(), dataRow["ID"].toString()));
};

/**
 * Vrátí požadovaný počet hráčů ze serveru a s tím i informaci, zdali je možno načíst další hráče.
 * @param count Počet hráčů ke stažení
 * @param getParticipants Funkce pro načtení daného počtu hráčů.
 * @returns Tuple kde na prvním místě je pole hráčů a na druhém místě je boolean informující o tom, zdali lze načíst další hráče.
 */
const batchifyParticipants = async (count: number, getParticipants: (count: number) => Promise<IParticipant[]>): Promise<[IParticipant[], boolean]> => {
    const participants = await getParticipants(count + 1);

    if (participants.length === count + 1) { // Načetli jsme count + 1 věcí, to znamená, že můžeme loadovat dál.
        participants.pop();
        return [participants, true];
    }

    return [participants, false];
};
